/**
 * Entitätengruppen und Subklassen-Auflösung für die IDS-Entity-Facette (#2310, #2311).
 *
 * <h2>Warum es das gibt</h2>
 * Die Entity-Facette kennt in IDS nur einen Einzelwert oder eine Aufzählung. Wer „alle Bauteile"
 * prüfen wollte, musste bisher jede IFC-Klasse von Hand eintragen — in IFC4 sind das 172. Und eine
 * Spezifikation auf `IfcWall` erfasst **keine** `IfcWallStandardCase`: `ifctester` vergleicht in
 * `Entity.filter` mit `include_subtypes=False` den Klassennamen exakt.
 *
 * <h2>Wie es abgebildet wird</h2>
 * Beides bleibt reine Editor-Bequemlichkeit: gespeichert wird eine gewöhnliche
 * `xs:enumeration`, das IDS bleibt also portabel und für Fremdwerkzeuge lesbar. Beim Laden werden
 * Gruppe bzw. Subklassen-Abschluss am Mengenvergleich **wiedererkannt** — dafür braucht es keine
 * BIC-eigene Erweiterung im XML.
 *
 * Die Auflösung hängt an der IFC-Version der Spezifikation und ist nicht optional:
 * `IfcBuildingElement` gibt es in IFC4X3 nicht mehr (dort heißt die Wurzel `IfcBuiltElement`), und
 * `IfcSpatialElement` fehlt in IFC2X3. Deckt eine Spezifikation mehrere Versionen ab, ist die
 * **Vereinigung** über deren Wurzeln gemeint — sonst fiele eine Klasse heraus, die nur in einer
 * der gewählten Versionen existiert.
 */

import {IFC_SUPERTYPES, IfcSchemaVersion} from './ifc-schema.generated';

/** Eine wählbare Sammlung von IFC-Klassen. */
export interface IfcEntityGroup {
  /** Stabiler Schlüssel für die Auswahl im Editor. */
  key: string;
  /** Anzeigename, wie im Ticket vorgegeben. */
  label: string;
  /**
   * Wurzelklasse je IFC-Version. Fehlt ein Eintrag, existiert die Gruppe in dieser Version nicht
   * und liefert dort keine Mitglieder.
   */
  roots: Partial<Record<IfcSchemaVersion, string>>;
  /** Kurzerklärung für den Tooltip. */
  hint: string;
}

/**
 * Die vier Gruppen aus dem Ticket.
 *
 * `IfcBuildingElement` wurde in IFC4X3 zu `IfcBuiltElement` umbenannt, und IFC2X3 kennt
 * `IfcSpatialElement` noch nicht — dort ist `IfcSpatialStructureElement` das Gegenstück.
 */
export const IFC_ENTITY_GROUPS: IfcEntityGroup[] = [
  {
    key: 'BUILDING_ELEMENTS',
    label: 'Bauelemente',
    roots: {IFC2X3: 'IfcBuildingElement', IFC4: 'IfcBuildingElement', IFC4X3: 'IfcBuiltElement'},
    hint: 'Alle Subklassen von IfcBuildingElement (ab IFC4X3: IfcBuiltElement)',
  },
  {
    key: 'DISTRIBUTION_ELEMENTS',
    label: 'TGA-Elemente',
    roots: {IFC2X3: 'IfcDistributionElement', IFC4: 'IfcDistributionElement', IFC4X3: 'IfcDistributionElement'},
    hint: 'Alle Subklassen von IfcDistributionElement',
  },
  {
    key: 'SPATIAL_ELEMENTS',
    label: 'Virtuelle Elemente',
    // IFC2X3 kennt IfcSpatialElement nicht; IfcSpatialStructureElement ist dort das Gegenstück.
    roots: {IFC2X3: 'IfcSpatialStructureElement', IFC4: 'IfcSpatialElement', IFC4X3: 'IfcSpatialElement'},
    hint: 'Alle Subklassen von IfcSpatialElement (IFC2X3: IfcSpatialStructureElement)',
  },
  {
    key: 'ALL_ELEMENTS',
    label: 'Alle Elemente',
    roots: {IFC2X3: 'IfcProduct', IFC4: 'IfcProduct', IFC4X3: 'IfcProduct'},
    hint: 'Alle Subklassen von IfcProduct',
  },
];

/** Fallback-Versionen, wenn eine Spezifikation keine IFC-Version gesetzt hat. */
const FALLBACK_VERSIONS: IfcSchemaVersion[] = ['IFC4'];

/** Cache für {@link subtypesOf}: reine Funktion über statisches Schema-Datenmaterial, sicher dauerhaft cachebar. */
const subtypesCache = new Map<string, string[]>();

/**
 * Subtyp-Abschluss einer Klasse **ohne** die Klasse selbst, über alle angegebenen Versionen.
 *
 * Reihenfolge ist die IFC-Schreibweise alphabetisch, damit das erzeugte XML stabil bleibt (sonst
 * gilt eine Regel nach dem Speichern grundlos als geändert).
 */
export function subtypesOf(entityName: string, versions: readonly string[]): string[] {
  const wanted = normalizeName(entityName);
  if (!wanted) return [];

  const cacheKey = `${wanted}|${schemaVersions(versions).join(',')}`;
  const cached = subtypesCache.get(cacheKey);
  if (cached) return cached;

  const result = new Set<string>();
  for (const version of schemaVersions(versions)) {
    const supertypes = IFC_SUPERTYPES[version];
    for (const candidate of Object.keys(supertypes)) {
      if (candidate.toUpperCase() === wanted) continue;
      if (ancestorsOf(candidate, supertypes).has(wanted)) result.add(candidate);
    }
  }
  const sorted = [...result].sort();
  subtypesCache.set(cacheKey, sorted);
  return sorted;
}

/**
 * Die Klasse selbst plus ihr Subtyp-Abschluss — genau das, was für „Subklassen mitprüfen" in die
 * Aufzählung geschrieben wird.
 */
export function withSubtypes(entityName: string, versions: readonly string[]): string[] {
  const own = properName(entityName, versions) ?? entityName.trim();
  return [own, ...subtypesOf(own, versions)].sort();
}

/** Cache für {@link groupMembers}, aus demselben Grund wie {@link subtypesCache}. */
const groupMembersCache = new Map<string, string[]>();

/** Mitglieder einer Gruppe: die Wurzel selbst plus ihr Subtyp-Abschluss. */
export function groupMembers(group: IfcEntityGroup, versions: readonly string[]): string[] {
  const cacheKey = `${group.key}|${schemaVersions(versions).join(',')}`;
  const cached = groupMembersCache.get(cacheKey);
  if (cached) return cached;

  const members = new Set<string>();
  for (const version of schemaVersions(versions)) {
    const root = group.roots[version];
    if (!root) continue;
    // Die Wurzel selbst gehört dazu: eine Datei kann z. B. ein IfcProduct-Exemplar führen, und
    // "Alle Elemente" soll dann nicht daran vorbeigehen.
    members.add(root);
    for (const subtype of subtypesOf(root, [version])) members.add(subtype);
  }
  const sorted = [...members].sort();
  groupMembersCache.set(cacheKey, sorted);
  return sorted;
}

/**
 * Erkennt eine Gruppe an ihrer Mitgliedermenge wieder.
 *
 * Nötig, weil im XML nur die Aufzählung steht. Verglichen wird als **Menge** und
 * case-insensitiv: `IdsXmlValidator.grossschreibeWerte()` schreibt die Werte beim Speichern auf
 * GROSSBUCHSTABEN, und die Reihenfolge ist im XML nicht bedeutungstragend.
 */
export function matchEntityGroup(
  values: readonly string[], versions: readonly string[],
): IfcEntityGroup | null {
  if (values.length === 0) return null;
  const actual = toKeySet(values);
  if (actual.size === 0) return null;

  for (const group of IFC_ENTITY_GROUPS) {
    if (sameSet(actual, toKeySet(groupMembers(group, versions)))) return group;
  }
  return null;
}

/**
 * Erkennt „Subklassen mitprüfen" wieder: die Aufzählung ist genau der Subtyp-Abschluss einer
 * kleineren Menge von Elternklassen.
 *
 * Zurückgegeben werden die Elternklassen, sonst `null`. Elternklasse ist jede Klasse der
 * Aufzählung, deren Vorfahren **nicht** ebenfalls darin stehen — die anderen sind die
 * expandierten Kinder. Erkannt wird nur, wenn die Expansion dieser Eltern die Aufzählung exakt
 * wiedergibt; andernfalls hat jemand einzelne Klassen von Hand ergänzt oder entfernt, und die
 * Aufzählung bleibt eine gewöhnliche.
 */
export function matchSubtypeClosure(
  values: readonly string[], versions: readonly string[],
): string[] | null {
  if (values.length < 2) return null;
  const actual = toKeySet(values);
  if (actual.size < 2) return null;

  const parents = values.filter(value => {
    const key = normalizeName(value);
    if (!key) return false;
    for (const version of schemaVersions(versions)) {
      for (const ancestor of ancestorsOf(value, IFC_SUPERTYPES[version])) {
        if (ancestor !== key && actual.has(ancestor)) return false;
      }
    }
    return true;
  });
  // Ohne echte Expansion ist es keine Subklassen-Aufzählung, sondern eine Liste von Geschwistern.
  if (parents.length === 0 || parents.length === values.length) return null;

  const expanded = new Set<string>();
  for (const parent of parents) {
    for (const name of withSubtypes(parent, versions)) expanded.add(name);
  }
  return sameSet(actual, toKeySet([...expanded]))
    ? parents.map(parent => properName(parent, versions) ?? parent).sort()
    : null;
}

/**
 * Tooltip-Text zu einer Gruppe: nennt die enthaltenen IFC-Klassen.
 *
 * Bei „Alle Elemente" sind das über 170 Namen — deshalb gekürzt. Ein Tooltip, der den halben
 * Bildschirm füllt, ist keine Hilfe.
 */
export function entityListTooltip(names: readonly string[], limit = 20): string {
  if (names.length === 0) return 'Keine IFC-Klassen';
  const shown = names.slice(0, limit).join(', ');
  const rest = names.length - limit;
  return rest > 0 ? `${shown} … und ${rest} weitere` : shown;
}

/** Korrekte IFC-Schreibweise eines (evtl. groß geschriebenen) Namens, falls im Schema bekannt. */
export function properName(entityName: string, versions: readonly string[]): string | null {
  const wanted = normalizeName(entityName);
  if (!wanted) return null;
  for (const version of schemaVersions(versions)) {
    for (const candidate of Object.keys(IFC_SUPERTYPES[version])) {
      if (candidate.toUpperCase() === wanted) return candidate;
    }
  }
  return null;
}

/** Alle Vorfahren (groß geschrieben) inklusive der Klasse selbst. */
function ancestorsOf(entityName: string, supertypes: Record<string, string>): Set<string> {
  const chain = new Set<string>();
  let current: string | undefined = properKey(entityName, supertypes);
  while (current && !chain.has(current.toUpperCase())) {
    chain.add(current.toUpperCase());
    current = supertypes[current];
  }
  return chain;
}

/** Schlüssel in der Schema-Tabelle zu einem beliebig geschriebenen Namen. */
function properKey(entityName: string, supertypes: Record<string, string>): string | undefined {
  const wanted = normalizeName(entityName);
  if (!wanted) return undefined;
  if (supertypes[entityName] !== undefined) return entityName;
  return Object.keys(supertypes).find(candidate => candidate.toUpperCase() === wanted);
}

/** Die IFC-Versionen einer Spezifikation, gefiltert auf die vom Schema bekannten. */
function schemaVersions(versions: readonly string[]): IfcSchemaVersion[] {
  const known = (versions ?? [])
    .map(version => (version ?? '').trim().toUpperCase())
    .filter((version): version is IfcSchemaVersion => version in IFC_SUPERTYPES);
  return known.length > 0 ? [...new Set(known)] : FALLBACK_VERSIONS;
}

function normalizeName(entityName: unknown): string {
  return typeof entityName === 'string' ? entityName.trim().toUpperCase() : '';
}

function toKeySet(values: readonly string[]): Set<string> {
  const keys = new Set<string>();
  for (const value of values) {
    const key = normalizeName(value);
    if (key) keys.add(key);
  }
  return keys;
}

function sameSet(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const value of a) if (!b.has(value)) return false;
  return true;
}
